/**
 * Airline Tail-Logo Cache (Logostream-backed)
 *
 * Wraps `GET /api/airline-logos?codes=...` with a two-tier cache:
 *   1. In-memory Map      — avoids redundant work inside a single page navigation
 *   2. localStorage       — persists results across reloads / PWA relaunches
 *                           (7-day TTL; airline data is stable between AIRAC cycles)
 *
 * Also dedupes concurrent requests for the same IATA code (in-flight Promises are shared).
 *
 * Shape returned per IATA — preserves legacy keys so existing <img src={...}> bindings work:
 *   {
 *     best, square, rectangular, tail, circular: all point to the SAME Logostream tail URL,
 *     name:   "IndiGo Airlines",
 *     iata:   "6E",
 *     icao:   "IGO",
 *     has_logo: true | false
 *   }
 */
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// v4 — normalise relative `/api/...` URLs to absolute backend URLs
// so <img src="/api/..."> doesn't accidentally hit the frontend origin.
const LS_KEY = 'acf_airline_logos_v4';
const TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

// Backend origin used to absolutize relative URLs returned by the backend.
// If REACT_APP_BACKEND_URL is empty (same-origin deployments), we keep them relative.
const BACKEND_ORIGIN = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
const abs = (u) => (u && typeof u === 'string' && u.startsWith('/') ? `${BACKEND_ORIGIN}${u}` : (u || ''));

// In-memory tier
const memory = new Map(); // iata -> entry
// In-flight request dedupe
const pending = new Map(); // iata -> Promise<entry>

// ── localStorage hydration ──────────────────────────────────────────────────
function _loadLS() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    // Drop stale entries at hydration time
    const now = Date.now();
    const fresh = {};
    for (const [iata, row] of Object.entries(parsed)) {
      if (row && row._ts && now - row._ts < TTL_MS) {
        fresh[iata] = row;
      }
    }
    return fresh;
  } catch {
    return {};
  }
}

function _saveLS(store) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(store));
  } catch {
    // localStorage can throw on quota — fail silently, we still have memory tier
  }
}

let lsStore = _loadLS();
// Seed the memory tier from localStorage so first lookups are instant
for (const [iata, row] of Object.entries(lsStore)) {
  memory.set(iata, row);
}

function _setEntry(iata, entry) {
  const stamped = { ...entry, _ts: Date.now() };
  memory.set(iata, stamped);
  lsStore[iata] = stamped;
  _saveLS(lsStore);
}

// ── Public API ──────────────────────────────────────────────────────────────

/**
 * Prefetch/resolve airline logos for a list of IATA codes.
 * Returns an object { IATA: entry } with ALL requested codes present.
 * Triggers a single network call for codes not already cached.
 */
export async function fetchAirlineLogos(codes) {
  if (!codes) return {};
  const list = (Array.isArray(codes) ? codes : String(codes).split(','))
    .map(c => (c || '').trim().toUpperCase())
    .filter(Boolean);
  if (list.length === 0) return {};

  const out = {};
  const missing = [];

  for (const code of list) {
    if (memory.has(code)) {
      out[code] = memory.get(code);
    } else if (pending.has(code)) {
      // Join an in-flight request for this code
      out[code] = null;
      missing.push(code);
    } else {
      missing.push(code);
    }
  }

  if (missing.length === 0) return out;

  // Partition: codes with a pending Promise we can reuse vs. codes we must fetch now
  const needsFetch = missing.filter(c => !pending.has(c));

  let fetchPromise = null;
  if (needsFetch.length > 0) {
    fetchPromise = (async () => {
      try {
        const resp = await axios.get(`${API}/airline-logos?codes=${needsFetch.join(',')}`, {
          // Browser may cache too (Cache-Control: 7d from backend)
          headers: { 'Accept': 'application/json' },
        });
        const data = (resp.data && resp.data.data) || {};
        for (const code of needsFetch) {
          const entry = data[code] || {
            best: '', square: '', rectangular: '', tail: '', circular: '',
            name: '', iata: code, icao: '', has_logo: false,
          };
          _setEntry(code, {
            ...entry,
            best: abs(entry.best),
            square: abs(entry.square),
            rectangular: abs(entry.rectangular),
            tail: abs(entry.tail),
            circular: abs(entry.circular),
          });
        }
      } catch (err) {
        // Network failure — stamp empty entries with a short TTL by NOT writing LS
        // Still put them in memory to prevent retry storms in the same session
        for (const code of needsFetch) {
          const empty = {
            best: '', square: '', rectangular: '', tail: '', circular: '',
            name: '', iata: code, icao: '', has_logo: false,
          };
          memory.set(code, empty);
        }
      }
    })();
    for (const code of needsFetch) pending.set(code, fetchPromise);
  }

  // Wait for all in-flight fetches that cover `missing`
  const waitFor = new Set();
  for (const code of missing) {
    if (pending.has(code)) waitFor.add(pending.get(code));
  }
  await Promise.all([...waitFor]);

  // Clean up pending entries we kicked off
  if (needsFetch.length > 0) {
    for (const code of needsFetch) pending.delete(code);
  }

  // Final read
  for (const code of missing) {
    out[code] = memory.get(code) || {
      best: '', square: '', rectangular: '', tail: '', circular: '',
      name: '', iata: code, icao: '', has_logo: false,
    };
  }
  return out;
}

/** Synchronous read — returns entry or null if not yet cached. */
export function getCachedAirlineLogo(iata) {
  if (!iata) return null;
  const code = String(iata).trim().toUpperCase();
  return memory.get(code) || null;
}

/** Returns the tail URL (or empty string) for an IATA code already in cache. */
export function getCachedLogoUrl(iata) {
  const e = getCachedAirlineLogo(iata);
  return (e && e.best) || '';
}

/** Manual cache bust (e.g. after an admin logo override). */
export function clearAirlineLogoCache() {
  memory.clear();
  pending.clear();
  lsStore = {};
  try { localStorage.removeItem(LS_KEY); } catch {}
}
